import React, { type FunctionComponent, type MouseEvent, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Button, Stack } from '@mui/material';

import StarIcon from 'components/Icons/Star';

import styles from './StarRating.module.scss';

export interface StarRatingProps {
  ariaLabelPrefix?: string;
  isLoading?: boolean;
  max?: number;
  onChange: (value: number) => void;
  value: number | undefined;
  variant?: 'monochrome' | 'forecast';
  size?: 'small' | 'medium' | 'large';
  minRating?: number;
}

interface StarButtonProps {
  ariaLabelPrefix: string;
  disabled: boolean;
  filled: boolean;
  index: number;
  onSelect: (event: MouseEvent<HTMLButtonElement>) => void;
}

const StarButton: FunctionComponent<StarButtonProps> = ({
  ariaLabelPrefix,
  disabled,
  filled,
  index,
  onSelect,
}) => {
  const ariaLabel = useMemo(
    () => `${ariaLabelPrefix} ${index} star${index > 1 ? 's' : ''}`,
    [ariaLabelPrefix, index],
  );

  return (
    <Button
      aria-label={ariaLabel}
      className={classNames(styles.starButton)}
      color="inherit"
      data-value={index}
      disableFocusRipple
      disableRipple
      disableTouchRipple
      disabled={disabled}
      onClick={onSelect}
      variant="text"
      data-testid={`star-${index}`}
    >
      <StarIcon
        className={classNames(styles.starIconFilled, {
          [styles.visible]: filled,
          [styles.hidden]: !filled,
        })}
        fill="inherit"
      />
      <StarIcon
        className={classNames(styles.starIconEmpty, {
          [styles.visible]: !filled,
          [styles.hidden]: filled,
        })}
        fill="inherit"
      />
    </Button>
  );
};

const StarRating: FunctionComponent<StarRatingProps> = ({
  ariaLabelPrefix = 'Rate',
  isLoading = false,
  max = 5,
  onChange,
  value,
  variant = 'monochrome',
  size = 'medium',
  minRating = 1,
}) => {
  const clampedRating = useMemo(() => {
    if (value === undefined) return 0;
    const min = minRating;
    return Math.min(Math.max(Math.round(value), min), max);
  }, [value, max, minRating]);

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { value: valueAttr } = event.currentTarget.dataset;
      if (valueAttr) {
        const next = Number(valueAttr);
        if (!Number.isNaN(next)) {
          onChange(next);
        }
      }
    },
    [onChange],
  );

  const stars = useMemo(
    () =>
      Array.from({ length: max }, (_, i) => {
        const index = i + 1;
        return (
          <li key={index}>
            <StarButton
              ariaLabelPrefix={ariaLabelPrefix}
              disabled={isLoading}
              filled={index <= clampedRating}
              index={index}
              onSelect={handleSelect}
            />
          </li>
        );
      }),
    [max, ariaLabelPrefix, isLoading, clampedRating, handleSelect],
  );

  return (
    <Stack
      alignItems="center"
      className={classNames(styles.starRating, {
        [styles.monochromeVariant]: variant === 'monochrome',
        [styles.forecastVariant]: variant === 'forecast',
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large',
      })}
      component="ul"
      data-rating={clampedRating}
      direction="row"
      justifyContent="center"
      spacing={0}
    >
      {stars}
    </Stack>
  );
};

export default StarRating;
